import { z } from 'zod/v4';

const energy = z.number().finite().nonnegative().max(1_000_000_000);
const month = z.number().int().min(1).max(12);
const profile = z.array(energy).length(12);

/** A financial scenario, not a verified utility tariff or an interval model. */
export const solarUsNetMeteringLedgerInputSchema = z.object({
  generation: z
    .object({
      monthlyAcKwh: profile,
      provenance: z.string().min(1).max(500),
    })
    .nullable(),
  demand: z.object({
    monthlyKwh: profile,
    provenance: z.string().min(1).max(500),
  }),
  siteTimeZone: z
    .string()
    .min(1)
    .refine((value) => {
      try {
        new Intl.DateTimeFormat('en', { timeZone: value });
        return value.includes('/');
      } catch {
        return false;
      }
    }, 'Enter an IANA site time zone'),
  timeZoneProvenance: z.string().min(1).max(500),
  startYear: z.number().int().min(1900).max(2200),
  startMonth: month,
  months: z.number().int().min(1).max(600),
  assumedSelfUseFraction: z.number().finite().min(0).max(1),
  retailCentsPerKwh: energy,
  compensation: z
    .discriminatedUnion('mode', [
      z.object({ mode: z.literal('flatExport'), exportCentsPerKwh: energy }),
      z.object({
        mode: z.literal('monthlyKwhBank'),
        wholesaleCentsPerKwh: energy,
        /** First month of the new credit year: April means settle at March close. */
        settlementMonth: month.default(4),
      }),
    ])
    .default({ mode: 'flatExport', exportCentsPerKwh: 7 }),
  annualDegradationPercent: z.number().finite().min(0).max(100).default(0.5),
  annualRetailGrowthPercent: z.number().finite().min(0).max(100).default(0),
  annualExportGrowthPercent: z.number().finite().min(0).max(100).default(0),
  costBasis: z.discriminatedUnion('kind', [
    z.object({
      kind: z.literal('complete'),
      systemCostUsd: energy,
      description: z.string().min(1),
    }),
    z.object({ kind: z.literal('incomplete'), description: z.string().min(1) }),
  ]),
});

export type SolarUsNetMeteringLedgerInput = z.input<
  typeof solarUsNetMeteringLedgerInputSchema
>;
export type SolarUsNetMeteringLedgerAssumptions = z.output<
  typeof solarUsNetMeteringLedgerInputSchema
>;

export type SolarUsNetMeteringLedgerRow = {
  period: string;
  operatingYear: number;
  generationKwh: number;
  demandKwh: number;
  selfUseKwh: number;
  importsKwh: number;
  exportsKwh: number;
  creditsUsedKwh: number;
  bankOpeningKwh: number;
  bankClosingKwh: number;
  settledKwh: number;
  settlementUsd: number;
  exportPaymentUsd: number;
  retailUsdPerKwh: number;
  exportUsdPerKwh: number;
  baselineCostUsd: number;
  modelEnergyCostUsd: number;
  avoidedEnergyBillUsd: number;
  benefitUsd: number;
};

export type SolarUsNetMeteringLedgerSnapshot = {
  schemaVersion: 1;
  engineVersion: 'us-net-metering-v1';
  currency: 'USD';
  assumptions: SolarUsNetMeteringLedgerAssumptions;
  rows: SolarUsNetMeteringLedgerRow[];
  totals: {
    baselineCostUsd: number;
    modelEnergyCostUsd: number;
    avoidedEnergyBillUsd: number;
    settlementUsd: number;
    exportPaymentUsd: number;
    benefitUsd: number;
    bankClosingKwh: number;
  };
  /** Full operating year only; partial periods never masquerade as annual. */
  firstYearBenefitUsd: number | null;
  /** First observed month recovering a complete stated cost, not scalar payback. */
  costRecoveredAfterMonths: number | null;
};

export type SolarUsNetMeteringLedgerResult =
  | { status: 'available'; snapshot: SolarUsNetMeteringLedgerSnapshot }
  | {
      status: 'unavailable';
      reason: 'missingGeneration' | 'invalidInput';
      message: string;
    };

/** Months are site-local calendar buckets supplied by the generation source. */
export const calculateSolarUsNetMeteringLedger = (
  input: SolarUsNetMeteringLedgerInput
): SolarUsNetMeteringLedgerResult => {
  const parsed = solarUsNetMeteringLedgerInputSchema.safeParse(input);
  if (!parsed.success) {
    return {
      status: 'unavailable',
      reason: 'invalidInput',
      message: parsed.error.issues
        .map((issue) => `${issue.path.join('.')}: ${issue.message}`)
        .join('; '),
    };
  }
  const assumptions = parsed.data;
  if (assumptions.generation === null) {
    return {
      status: 'unavailable',
      reason: 'missingGeneration',
      message:
        'Monthly generation is unavailable. Pricing can continue without a savings forecast.',
    };
  }
  const { compensation } = assumptions;
  const rows: SolarUsNetMeteringLedgerRow[] = [];
  let bank = 0;
  let runningBenefit = 0;
  let costRecoveredAfterMonths: number | null = null;
  // Convert cents once. All ledger money thereafter is USD, all bank entries kWh.
  const retailBase = assumptions.retailCentsPerKwh / 100;
  const exportBase =
    (compensation.mode === 'flatExport'
      ? compensation.exportCentsPerKwh
      : compensation.wholesaleCentsPerKwh) / 100;
  for (let offset = 0; offset < assumptions.months; offset++) {
    const calendarIndex = assumptions.startMonth - 1 + offset;
    const calendarMonth = calendarIndex % 12;
    const calendarYear = assumptions.startYear + Math.floor(calendarIndex / 12);
    const ageingYear = Math.floor(offset / 12);
    const generationKwh =
      assumptions.generation.monthlyAcKwh[calendarMonth] *
      (1 - assumptions.annualDegradationPercent / 100) ** ageingYear;
    const demandKwh = assumptions.demand.monthlyKwh[calendarMonth];
    const selfUseKwh = Math.min(
      generationKwh * assumptions.assumedSelfUseFraction,
      demandKwh
    );
    const importsKwh = demandKwh - selfUseKwh;
    const exportsKwh = generationKwh - selfUseKwh;
    const retailUsdPerKwh =
      retailBase *
      (1 + assumptions.annualRetailGrowthPercent / 100) ** ageingYear;
    const exportUsdPerKwh =
      exportBase *
      (1 + assumptions.annualExportGrowthPercent / 100) ** ageingYear;
    const bankOpeningKwh = bank;
    let creditsUsedKwh = 0;
    let settledKwh = 0;
    let exportPaymentUsd = 0;
    if (compensation.mode === 'monthlyKwhBank') {
      const available = bank + exportsKwh;
      creditsUsedKwh = Math.min(importsKwh, available);
      bank = Math.max(0, available - creditsUsedKwh);
      if (((calendarMonth + 1) % 12) + 1 === compensation.settlementMonth) {
        settledKwh = bank;
        bank = 0;
      }
    } else {
      exportPaymentUsd = exportsKwh * exportUsdPerKwh;
    }
    const settlementUsd = settledKwh * exportUsdPerKwh;
    const baselineCostUsd = demandKwh * retailUsdPerKwh;
    const importCostUsd = (importsKwh - creditsUsedKwh) * retailUsdPerKwh;
    const modelEnergyCostUsd = importCostUsd - settlementUsd - exportPaymentUsd;
    const avoidedEnergyBillUsd = baselineCostUsd - importCostUsd;
    const benefitUsd = baselineCostUsd - modelEnergyCostUsd;
    runningBenefit += benefitUsd;
    if (
      costRecoveredAfterMonths === null &&
      assumptions.costBasis.kind === 'complete' &&
      runningBenefit >= assumptions.costBasis.systemCostUsd
    ) {
      costRecoveredAfterMonths = offset + 1;
    }
    rows.push({
      period: `${calendarYear}-${String(calendarMonth + 1).padStart(2, '0')}`,
      operatingYear: ageingYear + 1,
      generationKwh,
      demandKwh,
      selfUseKwh,
      importsKwh,
      exportsKwh,
      creditsUsedKwh,
      bankOpeningKwh,
      bankClosingKwh: bank,
      settledKwh,
      settlementUsd,
      exportPaymentUsd,
      retailUsdPerKwh,
      exportUsdPerKwh,
      baselineCostUsd,
      modelEnergyCostUsd,
      avoidedEnergyBillUsd,
      benefitUsd,
    });
  }
  const sum = (
    field:
      | 'baselineCostUsd'
      | 'modelEnergyCostUsd'
      | 'avoidedEnergyBillUsd'
      | 'settlementUsd'
      | 'exportPaymentUsd'
      | 'benefitUsd'
  ) => rows.reduce((total, row) => total + row[field], 0);
  const snapshot: SolarUsNetMeteringLedgerSnapshot = {
    schemaVersion: 1,
    engineVersion: 'us-net-metering-v1',
    currency: 'USD',
    assumptions,
    rows,
    totals: {
      baselineCostUsd: sum('baselineCostUsd'),
      modelEnergyCostUsd: sum('modelEnergyCostUsd'),
      avoidedEnergyBillUsd: sum('avoidedEnergyBillUsd'),
      settlementUsd: sum('settlementUsd'),
      exportPaymentUsd: sum('exportPaymentUsd'),
      benefitUsd: sum('benefitUsd'),
      bankClosingKwh: bank,
    },
    firstYearBenefitUsd:
      rows.length >= 12
        ? rows.slice(0, 12).reduce((total, row) => total + row.benefitUsd, 0)
        : null,
    costRecoveredAfterMonths,
  };
  // A detached, frozen result is ready to persist verbatim. Rendering it must
  // never consult current account tariffs or recalculate an old engine version.
  Object.freeze(assumptions.generation.monthlyAcKwh);
  Object.freeze(assumptions.generation);
  Object.freeze(assumptions.demand.monthlyKwh);
  Object.freeze(assumptions.demand);
  Object.freeze(assumptions.compensation);
  Object.freeze(assumptions.costBasis);
  Object.freeze(assumptions);
  rows.forEach((row) => Object.freeze(row));
  Object.freeze(rows);
  Object.freeze(snapshot.totals);
  Object.freeze(snapshot);
  return { status: 'available', snapshot };
};
